// Settings.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { COUNTRIES } from './config';
import { isValidRelayUrl } from './utils/sanitize';

const Settings = ({ relays, addRelay, removeRelay }) => {
  const { publicKey } = useAuth();
  const { translate, country, changeCountry } = useLanguage();
  const [relayError, setRelayError] = useState('');
  const [newRelayInput, setNewRelayInput] = useState('');
  const navigate = useNavigate();

  const handleAddRelay = (relay) => {
    const trimmedRelay = relay.trim();
    setRelayError('');
    
    // Validate the relay URL
    if (!isValidRelayUrl(trimmedRelay)) {
      setRelayError('Invalid relay URL. Must be a secure WebSocket URL (wss://)');
      return;
    }
    
    // Check for duplicates
    if (relays.includes(trimmedRelay)) {
      setRelayError('This relay is already in your list');
      return;
    }
    
    // Check maximum relay limit
    if (relays.length >= 20) {
      setRelayError('Maximum 20 relays allowed');
      return;
    }
    
    addRelay(trimmedRelay);
    setNewRelayInput('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && newRelayInput) {
      handleAddRelay(newRelayInput);
    }
  };

  return (
    <div className="settings">
      <div className="settings-section">
        <div className="navigation-buttons">
          <button 
            className="settings-button primary"
            onClick={() => navigate('/account')}
          >
            {publicKey ? 'View Account' : 'Sign In'}
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h2>{translate('country')}</h2>
        <div className="country-selection">
          {Object.values(COUNTRIES).map((countryOption) => (
            <button
              key={countryOption.code}
              onClick={() => changeCountry(countryOption.code)}
              className={`country-button ${country === countryOption.code ? 'active' : ''}`}
            >
              <span className="country-flag">{countryOption.flag}</span>
              <span>{countryOption.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="settings-section">
        <h2>Relays</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Relays are servers that store and distribute Nostr events. Add trusted relays only.
        </p>
        <div className="relay-list">
          {relays.map((relay, index) => (
            <div key={index} className="relay-item">
              <span>{relay}</span>
              <button 
                onClick={() => removeRelay(relay)}
                className="remove-button"
                disabled={relays.length <= 1}
                title={relays.length <= 1 ? 'Must have at least one relay' : 'Remove relay'}
              >
                Remove
              </button>
            </div>
          ))}
          
          <div className="add-relay">
            <input
              type="url"
              placeholder="wss://relay.example.com"
              value={newRelayInput}
              onChange={(e) => setNewRelayInput(e.target.value)}
              onKeyPress={handleKeyPress}
              pattern="wss://.*"
            />
            <button 
              onClick={() => handleAddRelay(newRelayInput)}
              disabled={!newRelayInput}
            >
              Add Relay
            </button>
          </div>
          
          {relayError && (
            <p style={{ color: 'var(--error)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              {relayError}
            </p>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h2>Privacy</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Your private key is stored locally on your device. We never transmit or store your keys on any server.
        </p>
        <div className="navigation-buttons">
          <button 
            className="settings-button"
            onClick={() => {
              // Clear cached user data
              const keysToRemove = [];
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('user-')) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach(key => localStorage.removeItem(key));
              alert('Profile cache cleared successfully');
            }}
          >
            Clear Profile Cache
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
